/**
 * Sanitisation et validation des valeurs de conditions — par type et opérateur.
 */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;

export function digitsOnlyPhone(raw) {
    if (raw == null) return "";
    const s = String(raw).trim();
    const hasPlus = s.startsWith("+");
    const digits = s.replace(/\D/g, "");
    return hasPlus ? `+${digits}` : digits;
}

export function sanitizePhoneInput(raw) {
    if (raw == null) return "";
    const s = String(raw);
    const hasPlus = s.trim().startsWith("+");
    const digits = s.replace(/\D/g, "").slice(0, 15);
    if (!digits) return hasPlus ? "+" : "";
    return hasPlus ? `+${digits}` : digits;
}

export function isValidPhone(value) {
    const normalized = digitsOnlyPhone(value);
    if (!normalized) return false;
    const digits = normalized.replace(/\D/g, "");
    return digits.length >= 8 && digits.length <= 15;
}

export function sanitizeEmailInput(raw) {
    if (raw == null) return "";
    return String(raw).trim().toLowerCase();
}

export function isValidEmail(value) {
    const v = sanitizeEmailInput(value);
    return v.length > 0 && EMAIL_RE.test(v);
}

export function isValidUri(value) {
    const v = String(value || "").trim();
    if (!v) return false;
    try {
        const u = new URL(v.includes("://") ? v : `https://${v}`);
        return Boolean(u.hostname);
    } catch {
        return /^[a-z][a-z0-9+.-]*:/i.test(v);
    }
}

export function isValidUuid(value) {
    return UUID_RE.test(String(value || "").trim());
}

export function isValidIpv4(value) {
    return IPV4_RE.test(String(value || "").trim());
}

export function isValidTime(value) {
    return TIME_RE.test(String(value || "").trim());
}

export function isEmptyValue(value) {
    if (value === "" || value == null) return true;
    if (Array.isArray(value)) return value.length === 0 || value.every((v) => v === "" || v == null);
    return false;
}

export function sanitizeScalarOnChange(inputKind, raw) {
    switch (inputKind) {
        case "phone":
            return sanitizePhoneInput(raw);
        case "email":
            return String(raw ?? "").replace(/\s/g, "");
        case "number":
            return raw;
        default:
            return raw;
    }
}

export function validateScalarValue(inputKind, value, { allowEmpty = true } = {}) {
    if (isEmptyValue(value)) {
        return allowEmpty ? { valid: true, error: null } : { valid: false, error: "Valeur requise." };
    }
    const v = value;
    switch (inputKind) {
        case "email":
            return isValidEmail(v)
                ? { valid: true, error: null }
                : { valid: false, error: "Adresse email invalide (ex. client@exemple.com)." };
        case "phone":
            return isValidPhone(v)
                ? { valid: true, error: null }
                : { valid: false, error: "Numéro invalide (8 à 15 chiffres, ex. 697907060 ou 237697907060)." };
        case "uri":
            return isValidUri(v)
                ? { valid: true, error: null }
                : { valid: false, error: "URL ou URI invalide (ex. https://exemple.com)." };
        case "uuid":
            return isValidUuid(v)
                ? { valid: true, error: null }
                : { valid: false, error: "UUID invalide (ex. 550e8400-e29b-41d4-a716-446655440000)." };
        case "ipv4":
            return isValidIpv4(v)
                ? { valid: true, error: null }
                : { valid: false, error: "Adresse IPv4 invalide (ex. 192.168.0.1)." };
        case "time":
            return isValidTime(v)
                ? { valid: true, error: null }
                : { valid: false, error: "Heure invalide (format HH:mm)." };
        case "number": {
            const n = Number(v);
            return !Number.isNaN(n)
                ? { valid: true, error: null }
                : { valid: false, error: "Nombre attendu." };
        }
        default:
            return { valid: true, error: null };
    }
}

export function validateListValue(inputKind, values, { allowEmpty = true } = {}) {
    const list = Array.isArray(values) ? values : [];
    if (!list.length) {
        return allowEmpty ? { valid: true, error: null } : { valid: false, error: "Au moins une valeur requise." };
    }
    for (let i = 0; i < list.length; i += 1) {
        const item = list[i];
        if (item === "" || item == null) continue;
        const check = validateScalarValue(inputKind, item, { allowEmpty: false });
        if (!check.valid) {
            return { valid: false, error: `Élément ${i + 1} : ${check.error}` };
        }
    }
    return { valid: true, error: null };
}

export function validateBetweenValue(inputKind, value, { allowEmpty = true } = {}) {
    const arr = Array.isArray(value) ? value : ["", ""];
    const [lo, hi] = arr;
    if (isEmptyValue(lo) && isEmptyValue(hi)) {
        return allowEmpty ? { valid: true, error: null } : { valid: false, error: "Les deux bornes sont requises." };
    }
    const loCheck = validateScalarValue(inputKind, lo, { allowEmpty: isEmptyValue(hi) });
    if (!loCheck.valid) return { valid: false, error: `Borne basse : ${loCheck.error}` };
    const hiCheck = validateScalarValue(inputKind, hi, { allowEmpty: isEmptyValue(lo) });
    if (!hiCheck.valid) return { valid: false, error: `Borne haute : ${hiCheck.error}` };
    return { valid: true, error: null };
}

export function validateConditionValue(descriptor, operator, value, { allowEmpty = true } = {}) {
    const inputKind = descriptor?.inputKind || descriptor?.valueKind || "string";
    const itemsKind = descriptor?.itemsKind || inputKind;

    if (operator === "exists") return { valid: true, error: null };
    if (operator === "between") return validateBetweenValue(inputKind, value, { allowEmpty });
    if (operator === "in") return validateListValue(inputKind, value, { allowEmpty });

    if (inputKind === "array") {
        if (operator === "contains") {
            return validateScalarValue(itemsKind, value, { allowEmpty });
        }
        return validateListValue(itemsKind, Array.isArray(value) ? value : (value != null && value !== "" ? [value] : []), { allowEmpty });
    }

    if (inputKind === "object") {
        if (isEmptyValue(value)) return allowEmpty ? { valid: true, error: null } : { valid: false, error: "Objet JSON requis." };
        if (typeof value === "object") return { valid: true, error: null };
        try {
            const parsed = JSON.parse(String(value));
            return typeof parsed === "object" && parsed !== null
                ? { valid: true, error: null }
                : { valid: false, error: "Objet JSON attendu." };
        } catch {
            return { valid: false, error: "JSON invalide." };
        }
    }

    return validateScalarValue(inputKind, value, { allowEmpty });
}
